// ============================================================================
// appCheckPolicy.js — pure App Check bootstrap decision (S2)
//
// The "should we initialize App Check, and with what debug posture" logic,
// extracted from appCheck.js so it can be unit-tested under plain Node. This
// module is deliberately PURE: no Firebase imports, no JSX, no import.meta —
// callers pass the build-time env values in, it returns a plain decision.
//
// Safety rules encoded here:
//   - No reCAPTCHA site key  → do NOT initialize (App Check simply absent; the
//     app must keep working — never crash on a missing key).
//   - Debug tokens are DEV-ONLY: an explicit VITE_APPCHECK_DEBUG_TOKEN in dev,
//     otherwise `true` to let Firebase mint a local debug token. NEVER in prod.
// ============================================================================

/**
 * Decide whether/how to initialize Firebase App Check from build-time env.
 *
 * @param {{ siteKey?: string|null, isDev?: boolean, debugToken?: string|null }} [env]
 * @returns {{ shouldInitialize: boolean, debugToken: (string|true|null) }}
 *   shouldInitialize — false when no reCAPTCHA site key is configured.
 *   debugToken       — dev only: explicit token or `true`; always null in prod.
 */
export function resolveAppCheckBootstrap(env = {}) {
  const siteKey = env.siteKey;

  // Missing/empty key → safe no-op. The client keeps working with App Check
  // simply not installed (monitor-mode rollout: server enforcement stays off).
  if (!siteKey) {
    return { shouldInitialize: false, debugToken: null };
  }

  const isDev = env.isDev === true;
  const explicitDebug = env.debugToken || null;

  return {
    shouldInitialize: true,
    debugToken: isDev ? (explicitDebug || true) : null,
  };
}

export default resolveAppCheckBootstrap;
